"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ViewerController = void 0;
const plattar_api_1 = require("@plattar/plattar-api");
const product_ar_1 = require("../../ar/product-ar");
const scene_ar_1 = require("../../ar/scene-ar");
const scene_product_ar_1 = require("../../ar/scene-product-ar");
const configurator_state_1 = require("../../util/configurator-state");
const util_1 = require("../../util/util");
const plattar_controller_1 = require("./plattar-controller");
/**
 * Manages an instance of the <plattar-viewer> HTML Element
 */
class ViewerController extends plattar_controller_1.PlattarController {
    constructor(parent) {
        super(parent);
    }
    onAttributesUpdated() {
        const state = this._state;
        // re-render the QR Code when attributes have changed
        if (state === plattar_controller_1.ControllerState.QRCode) {
            this.startQRCode(this._prevQROpt);
            return;
        }
        // use the messenger function to change variation when attributes have changed
        if (state === plattar_controller_1.ControllerState.Renderer) {
            const viewer = this._element;
            if (viewer) {
                const productID = (this.getAttribute("product-id") || this.getAttribute("scene-product-id"));
                const variationID = this.getAttribute("variation-id");
                if (productID && variationID && viewer.messenger) {
                    viewer.messenger.selectVariation(productID, variationID);
                }
            }
            return;
        }
    }
    startViewerQRCode(options) {
        return new Promise((accept, reject) => {
            // remove the old renderer instance if any
            this.removeRenderer();
            const sceneID = this.getAttribute("scene-id");
            if (sceneID) {
                const opt = options || this._GetDefaultQROptions();
                const viewer = document.createElement("plattar-qrcode");
                // required attributes with defaults for plattar-viewer node
                const width = this.getAttribute("width") || "500px";
                const height = this.getAttribute("height") || "500px";
                viewer.setAttribute("width", width);
                viewer.setAttribute("height", height);
                if (opt.color) {
                    viewer.setAttribute("color", opt.color);
                }
                if (opt.margin) {
                    viewer.setAttribute("margin", "" + opt.margin);
                }
                if (opt.qrType) {
                    viewer.setAttribute("qr-type", opt.qrType);
                }
                viewer.setAttribute("shorten", (opt.shorten && (opt.shorten === true || opt.shorten === "true")) ? "true" : "false");
                let dst = plattar_api_1.Server.location().base + "renderer/viewer.html?scene_id=" + sceneID;
                // optional attributes
                const productID = (this.getAttribute("product-id") || this.getAttribute("scene-product-id"));
                const variationID = this.getAttribute("variation-id");
                const variationSKU = this.getAttribute("variation-sku");
                const showAR = this.getAttribute("show-ar");
                if (productID) {
                    dst += "&productId=" + productID;
                }
                if (variationID) {
                    dst += "&variationId=" + variationID;
                }
                if (variationSKU) {
                    dst += "&variationSku=" + variationSKU;
                }
                if (showAR) {
                    dst += "&show_ar=" + showAR;
                }
                viewer.setAttribute("url", opt.url || dst);
                viewer.onload = () => {
                    return accept(viewer);
                };
                this.append(viewer);
                this._element = viewer;
                this._state = plattar_controller_1.ControllerState.QRCode;
                this._prevQROpt = opt;
                return;
            }
            return reject(new Error("ViewerController.startQRCode() - minimum required attributes not set, use scene-id as a minimum"));
        });
    }
    startRenderer() {
        return new Promise((accept, reject) => {
            // remove the old renderer instance if any
            this.removeRenderer();
            const sceneID = this.getAttribute("scene-id");
            if (sceneID) {
                // required attributes with defaults for plattar-viewer node
                const width = this.getAttribute("width") || "500px";
                const height = this.getAttribute("height") || "500px";
                const server = this.getAttribute("server") || "production";
                const viewer = document.createElement("plattar-viewer");
                viewer.setAttribute("width", width);
                viewer.setAttribute("height", height);
                viewer.setAttribute("server", server);
                viewer.setAttribute("scene-id", sceneID);
                // optional attributes
                const productID = (this.getAttribute("product-id") || this.getAttribute("scene-product-id"));
                const variationID = this.getAttribute("variation-id");
                const variationSKU = this.getAttribute("variation-sku");
                const showAR = this.getAttribute("show-ar");
                if (productID) {
                    viewer.setAttribute("product-id", productID);
                }
                if (variationID) {
                    viewer.setAttribute("variation-id", variationID);
                }
                if (variationSKU) {
                    viewer.setAttribute("variation-sku", variationSKU);
                }
                if (showAR) {
                    viewer.setAttribute("show-ar", showAR);
                }
                viewer.onload = () => {
                    return accept(viewer);
                };
                this.append(viewer);
                this._element = viewer;
                this._state = plattar_controller_1.ControllerState.Renderer;
                return;
            }
            return reject(new Error("ViewerController.startRenderer() - minimum required attributes not set, use scene-id as a minimum"));
        });
    }
    initAR() {
        return new Promise((accept, reject) => {
            if (!util_1.Util.canAugment()) {
                return reject(new Error("ViewerController.initAR() - cannot proceed as AR not available in context"));
            }
            const productID = this.getAttribute("product-id");
            // use product-id if available
            if (productID) {
                const variationID = this.getAttribute("variation-id");
                const variationSKU = this.getAttribute("variation-sku");
                const product = new product_ar_1.ProductAR(productID, variationID, variationSKU);
                return product.init().then(accept).catch(reject);
            }
            const sceneProductID = this.getAttribute("scene-product-id");
            // use scene-product-id if available
            if (sceneProductID) {
                const variationID = this.getAttribute("variation-id");
                const variationSKU = this.getAttribute("variation-sku");
                const product = new scene_product_ar_1.SceneProductAR(sceneProductID, variationID, variationSKU);
                return product.init().then(accept).catch(reject);
            }
            const sceneID = this.getAttribute("scene-id");
            // fallback to using default SceneAR implementation
            if (sceneID) {
                // special case - check if scene only has a single product, if so
                // we need to use provided variation-id or variation-sku to override
                const variationID = this.getAttribute("variation-id");
                const variationSKU = this.getAttribute("variation-sku");
                // just do scene-ar if variation ID and variation SKU is not set
                if (!variationID && !variationSKU) {
                    const sceneAR = new scene_ar_1.SceneAR(sceneID);
                    return sceneAR.init().then(accept).catch(reject);
                }
                // otherwise decode scene
                return configurator_state_1.ConfiguratorState.decodeScene(sceneID).then((state) => {
                    const firstProduct = state.first();
                    if (state.length > 1 || !firstProduct) {
                        return reject(new Error("ViewerController.initAR() - single product required to override variation-id or variation-sku"));
                    }
                    const product = new scene_product_ar_1.SceneProductAR(firstProduct.scene_product_id, variationID, variationSKU);
                    return product.init().then(accept).catch(reject);
                }).catch(reject);
            }
            return reject(new Error("ViewerController.initAR() - minimum required attributes not set, use scene-id as a minimum"));
        });
    }
    removeRenderer() {
        if (this._element) {
            this._element.remove();
            this._element = null;
            return true;
        }
        return false;
    }
    get element() {
        return this._element;
    }
}
exports.ViewerController = ViewerController;
